import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import { listStaff } from "../api/staffs";
import "./ReportsSectionCompact.css";
import {
  exportStaffPerformanceCsv,
  getStaffPerformanceReport,
} from "../api/reports";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import { getSalonTimezone } from "../utils/salonTimezone";
import useTableSort from "../hooks/useTableSort";
import { STAFF_PERFORMANCE_TOOLTIPS } from "../constants/reportTooltips";
import { REPORT_HELP } from "../constants/reportHelpContent";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // Fall back to local time when the timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

const formatNumber = (value, digits = 0) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) {
    return digits > 0 ? (0).toFixed(digits) : "0";
  }
  return num.toFixed(digits);
};

const formatPercent = (value) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "0.0%";
  return `${num.toFixed(1)}%`;
};

const HOURS_KEYS = new Set(["available_hours", "booked_hours", "clocked_hours"]);
const MONEY_KEYS = new Set([
  "net_sales",
  "tips_total",
  "tip_per_ticket",
  "refunds_total",
  "sales_per_hour",
]);
const PERCENT_KEYS = new Set([
  "utilization_pct",
  "tip_rate_pct",
  "refund_rate_pct",
  "rebook_rate_pct",
]);
const COUNT_KEYS = new Set(["ticket_count", "served_clients", "rebooked_clients"]);

const COLUMNS = [
  { key: "staff_name", label: "Staff" },
  { key: "available_hours", label: "Available Hours" },
  { key: "booked_hours", label: "Booked Hours" },
  { key: "clocked_hours", label: "Clocked Hours" },
  { key: "utilization_pct", label: "Utilization %" },
  { key: "net_sales", label: "Net Sales" },
  { key: "ticket_count", label: "Tickets" },
  { key: "tips_total", label: "Tips" },
  { key: "tip_per_ticket", label: "Tip / Ticket" },
  { key: "tip_rate_pct", label: "Tip Rate %" },
  { key: "refunds_total", label: "Refunds" },
  { key: "refund_rate_pct", label: "Refund Rate %" },
  { key: "sales_per_hour", label: "Sales / Hour" },
  { key: "served_clients", label: "Served Clients" },
  { key: "rebooked_clients", label: "Rebooked Clients" },
  { key: "rebook_rate_pct", label: "Rebook Rate %" },
];

export default function StaffPerformanceReport() {
  const navigate = useNavigate();
  const { currentSalon, user, loading: authLoading } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.STAFF_PERFORMANCE;

  const resolvedSalonId = useMemo(() => {
    const raw = currentSalon?.id ?? currentSalon?.salon_id ?? user?.salon_id ?? null;
    if (raw === null || raw === undefined || raw === "") return null;
    const asNum = Number(raw);
    return Number.isFinite(asNum) ? asNum : null;
  }, [currentSalon, user?.salon_id]);

  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);
  const today = useMemo(() => formatDateISO(new Date(), salonTimezone), [salonTimezone]);
  const defaultStart = useMemo(
    () => formatDateISO(addDays(new Date(), -6), salonTimezone),
    [salonTimezone]
  );

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/manager/reports", { replace: true });
  };
  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={STAFF_PERFORMANCE_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [rebookWindowDays, setRebookWindowDays] = useState(30);
  const [hoursBasis, setHoursBasis] = useState("time_clock");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hasAutoRunRef = useRef(false);

  useEffect(() => {
    hasAutoRunRef.current = false;
  }, [resolvedSalonId]);

  useEffect(() => {
    if (authLoading) return;
    if (resolvedSalonId) return;
    setError("Select a salon to view reports.");
    const timeout = setTimeout(() => {
      navigate("/my-salons", { replace: true });
    }, 600);
    return () => clearTimeout(timeout);
  }, [authLoading, resolvedSalonId, navigate]);

  useEffect(() => {
    let isMounted = true;
    const loadStaff = async () => {
      if (!resolvedSalonId) {
        setStaffOptions([]);
        return;
      }
      setStaffLoading(true);
      try {
        const staff = await listStaff({ is_active: true });
        if (!isMounted) return;
        setStaffOptions(Array.isArray(staff) ? staff : []);
      } catch (err) {
        if (!isMounted) return;
        setStaffOptions([]);
        setError((prev) => prev || "Unable to load staff for this salon.");
      } finally {
        if (isMounted) setStaffLoading(false);
      }
    };
    if (!authLoading && resolvedSalonId) {
      loadStaff();
    }
    return () => {
      isMounted = false;
    };
  }, [authLoading, resolvedSalonId]);

  const dateRangeError = useMemo(() => {
    if (!startDate || !endDate) return "";
    return endDate < startDate ? "End date cannot be before start date." : "";
  }, [startDate, endDate]);

  const payloadBase = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      employee_ids: employeeIds,
      rebook_window_days: Math.max(Number(rebookWindowDays) || 0, 0),
      hours_basis: hoursBasis,
    }),
    [startDate, endDate, employeeIds, rebookWindowDays, hoursBasis]
  );

  const fetchReport = useCallback(
    async (payloadOverride = null) => {
      if (authLoading) return;
      if (!resolvedSalonId) {
        setError(
          "Unable to determine your current salon. Please refresh or re-select your salon."
        );
        return;
      }
      if (dateRangeError) {
        setError(dateRangeError);
        return;
      }
      const requestPayload = payloadOverride || payloadBase;
      setLoading(true);
      setError("");
      try {
        const data = await getStaffPerformanceReport(requestPayload);
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setSummary(data?.summary || null);
      } catch (err) {
        setRows([]);
        setSummary(null);
        setError(err?.response?.data?.detail || "Unable to load staff performance report.");
      } finally {
        setLoading(false);
      }
    },
    [authLoading, resolvedSalonId, dateRangeError, payloadBase]
  );

  useEffect(() => {
    if (authLoading || !resolvedSalonId || hasAutoRunRef.current) return;
    hasAutoRunRef.current = true;
    fetchReport(payloadBase);
  }, [authLoading, resolvedSalonId, fetchReport, payloadBase]);

  const handleToggleEmployee = (staffId) => {
    setEmployeeIds((prev) =>
      prev.includes(staffId)
        ? prev.filter((entry) => entry !== staffId)
        : [...prev, staffId]
    );
  };

  const handleExport = async () => {
    if (dateRangeError) {
      setError(dateRangeError);
      return;
    }
    try {
      await exportStaffPerformanceCsv(payloadBase);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to export staff performance CSV.");
    }
  };

  const tiles = useMemo(
    () => [
      { label: "Staff", value: summary?.staff_count ?? rows.length },
      { label: "Net Sales", value: formatMoney(summary?.total_net_sales) },
      { label: "Sales / Hour", value: formatMoney(summary?.overall_sales_per_hour) },
      { label: "Utilization", value: formatPercent(summary?.overall_utilization_pct) },
      { label: "Rebook Rate", value: formatPercent(summary?.overall_rebook_rate_pct) },
      { label: "Tickets", value: formatNumber(summary?.total_tickets, 0) },
    ],
    [summary, rows.length]
  );

  const formatCellValue = (row, key) => {
    const value = row?.[key];
    if (MONEY_KEYS.has(key)) return formatMoney(value);
    if (PERCENT_KEYS.has(key)) return formatPercent(value);
    if (HOURS_KEYS.has(key)) return formatNumber(value, 2);
    if (COUNT_KEYS.has(key)) return formatNumber(value, 0);
    return value ?? "--";
  };

  const columnsMap = useMemo(() => {
    const map = {};
    COLUMNS.forEach((col) => {
      let type = "text";
      if (HOURS_KEYS.has(col.key)) type = "number";
      if (MONEY_KEYS.has(col.key)) type = "number";
      if (PERCENT_KEYS.has(col.key)) type = "percent";
      if (COUNT_KEYS.has(col.key)) type = "number";
      map[col.key] = { type, getValue: (row) => row?.[col.key] };
    });
    return map;
  }, []);

  const staffSort = useTableSort({
    rows,
    columns: columnsMap,
    fallbackKey: "staff_name",
  });

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">Staff Performance</div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id="staff-performance-overview"
              className={getSectionClassName("staff-performance-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_staff_performance"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}

            <div
              id="staff-performance-filters"
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
                "staff-performance-filters"
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Date From", "start_date")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Date To", "end_date")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Rebook Window (days)", "rebook_window")}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={rebookWindowDays}
                    onChange={(e) => setRebookWindowDays(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Hours Basis", "hours_basis")}
                  <select
                    value={hoursBasis}
                    onChange={(e) => setHoursBasis(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    <option value="time_clock">Time Clock Hours</option>
                    <option value="booked">Booked Hours</option>
                  </select>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  {renderFieldLabel("Employees", "employees")}
                  <button
                    type="button"
                    className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                    onClick={() => setEmployeeIds([])}
                  >
                    Clear
                  </button>
                </div>
                <div className="mt-3 max-h-44 overflow-y-auto space-y-2">
                  {staffOptions.map((staff) => (
                    <label
                      key={staff.id}
                      className="flex items-center gap-2 text-sm text-slate-700"
                    >
                      <input
                        type="checkbox"
                        checked={employeeIds.includes(staff.id)}
                        onChange={() => handleToggleEmployee(staff.id)}
                      />
                      {staff.display_name}
                    </label>
                  ))}
                  {staffLoading || authLoading ? (
                    <div className="text-sm text-slate-500">Loading staff...</div>
                  ) : resolvedSalonId && !staffOptions.length ? (
                    <div className="text-sm text-slate-500">No staff found.</div>
                  ) : null}
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  {employeeIds.length ? `${employeeIds.length} selected` : "All employees"}
                </div>
              </div>

              {dateRangeError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                  {dateRangeError}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <div className="text-xs text-slate-500">Salon timezone: {salonTimezone}</div>
                <div className="ml-auto flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fetchReport(payloadBase)}
                    className={`${BTN_PRIMARY} min-w-[140px]`}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Run Report"}
                  </button>
                  <button
                    id="staff-performance-export"
                    type="button"
                    onClick={handleExport}
                    className={`${BTN_OUTLINE} min-w-[140px] ${getSectionClassName(
                      "staff-performance-export"
                    )}`}
                    disabled={loading}
                  >
                    Export CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/manager/reports")}
                    className={`${BTN_OUTLINE} min-w-[140px]`}
                  >
                    Back to Reports
                  </button>
                </div>
              </div>
            </div>

            <div
              id="staff-performance-kpis"
              className={`grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4 ${getSectionClassName(
                "staff-performance-kpis"
              )}`}
            >
              {tiles.map((tile) => (
                <div
                  key={tile.label}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    {tile.label}
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">
                    {tile.value}
                  </div>
                </div>
              ))}
            </div>

            <div
              id="staff-performance-details"
              className={`staff-perf-table rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(
                "staff-performance-details"
              )}`}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      {COLUMNS.map((col) => (
                        <SortableTh
                          key={col.key}
                          label={col.label}
                          sortKey={col.key}
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          tooltip={STAFF_PERFORMANCE_TOOLTIPS[`column_${col.key}`]}
                          className="px-4 py-3 text-center"
                          align="center"
                        />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {staffSort.sortedRows.map((row) => (
                      <tr key={row.staff_id} className="border-t border-slate-100">
                        {COLUMNS.map((col) => (
                          <td
                            key={`${row.staff_id}-${col.key}`}
                            className="px-4 py-3 text-center text-slate-700"
                          >
                            {formatCellValue(row, col.key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {!staffSort.sortedRows.length && (
                      <tr>
                        <td
                          colSpan={COLUMNS.length}
                          className="px-4 py-8 text-center text-slate-500"
                        >
                          {loading ? "Loading staff performance..." : "No results for this range."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
